/**
 * Search Management - Moon Moon Agency
 * Handles search overlay functionality
 */

/*********************************************************************************************
 * 🔍 SEARCH MODULE
 */
const SearchManager = {
	// Local configuration
	config: {
		selectors: {
			searchContainer: '[data-search-container]',
			searchInput: '[data-search-input]',
			searchToggle: '[data-search-toggle]',
			searchClose: '[data-search-close]',
			searchClear: '[data-search-clear]',
			predictiveSearch: '[data-predictive-search]',
			predictiveSearchRoot: '.mm-predictive-search',
			header: '[data-mm-header]'
		},
		classes: {
			stopScrolling: 'stop-scrolling',
			open: 'mm-open',
			headerTransparent: 'mm-header--transparent',
			headerLight: 'mm-header--light'
		},
		timing: {
			searchBlurDelay: 100
		}
	},

	// Focus trap state
	focusTrap: {
		isActive: false,
		triggerElement: null,
		firstFocusable: null,
		lastFocusable: null,
		handleKeyDown: null
	},

	/**
	 * Initialize the search module
	 */
	init() {
		const search = window.getElement(this.config.selectors.searchContainer);
		if (!search) return;

		this._bindEvents();
		window.log('info', 'SearchManager', 'Initialized');
	},

	/**
	 * Bind all static event listeners (called once at init)
	 */
	_bindEvents() {
		// Close button (background overlay or dedicated close btn)
		const searchCloseBtn = window.getElement(this.config.selectors.searchClose);
		if (searchCloseBtn) {
			searchCloseBtn.addEventListener('click', () => this.hide());
		}

		// Clear button (inside search input)
		const searchClearBtn = window.getElement(this.config.selectors.searchClear);
		if (searchClearBtn) {
			searchClearBtn.addEventListener('click', () => this.hide());
		}

		// Click en dehors de <predictive-search> ferme la recherche — le bouton
		// toggle est exclu pour ne pas entrer en conflit avec son propre handler
		// (mm_navigation.js), qui gère deja l'ouverture/fermeture au click.
		// Phase de CAPTURE (3e argument `true`) : plusieurs modules (cartes
		// produit, panier, Splide) font `stopPropagation()` sur leurs propres
		// clics en phase de bulle. En bulle, ce listener ne les verrait jamais ;
		// en capture, il s'execute avant eux et la fermeture n'est jamais bloquee.
		document.addEventListener('click', (e) => {
			const search = window.getElement(this.config.selectors.searchContainer);
			if (!search?.classList.contains(this.config.classes.open)) return;
			if (e.target.closest(this.config.selectors.predictiveSearchRoot)) return;
			if (e.target.closest(this.config.selectors.searchToggle)) return;

			// Ce premier clic "outside" ne doit QUE fermer la recherche — pas
			// activer l'element en dessous (lien de section, carte produit...).
			// Sans ce blocage, un clic hors du header (non couvert par le filtre,
			// cf. mm-section.css) fermait la recherche ET suivait le lien.
			e.preventDefault();
			e.stopPropagation();
			this.hide();
		}, true);

		// Predictive search touchstart — enregistré une seule fois (pas dans show())
		const predictiveSearch = window.getElement(this.config.selectors.predictiveSearch);
		if (predictiveSearch) {
			predictiveSearch.addEventListener('touchstart', () => {
				const searchInput = window.getElement(this.config.selectors.searchInput);
				if (searchInput) {
					setTimeout(() => searchInput.blur(), this.config.timing.searchBlurDelay);
				}
			}, { passive: true });
		}
	},

	/**
	 * Update aria-expanded on all search toggle buttons
	 */
	_updateToggleAria(isOpen) {
		window.getElements(this.config.selectors.searchToggle).forEach((btn) => {
			btn.setAttribute('aria-expanded', String(isOpen));
		});
	},

	/**
	 * Setup focus trap for search overlay
	 */
	setupFocusTrap(searchContainer, triggerElement) {
		// Retire un trap encore actif (double open sans close) : sinon la reference
		// handleKeyDown est ecrasee et l'ancien listener devient impossible a retirer.
		this.removeFocusTrap();

		const focusableElements = searchContainer.querySelectorAll(window.FOCUSABLE_SELECTOR);

		if (focusableElements.length === 0) return;

		this.focusTrap.triggerElement = triggerElement;
		this.focusTrap.firstFocusable = focusableElements[0];
		this.focusTrap.lastFocusable = focusableElements[focusableElements.length - 1];

		// Handle Tab and Escape keys
		this.focusTrap.handleKeyDown = (e) => {
			// Escape key closes search
			if (e.key === 'Escape') {
				e.preventDefault();
				// hide() retire le trap et remet triggerElement à null : le mémoriser avant
				const trigger = this.focusTrap.triggerElement;
				this.hide();
				trigger?.focus();
				return;
			}

			// Tab key traps focus
			if (e.key !== 'Tab') return;

			if (e.shiftKey) {
				// Shift + Tab: going backwards
				if (document.activeElement === this.focusTrap.firstFocusable) {
					e.preventDefault();
					this.focusTrap.lastFocusable?.focus();
				}
			} else {
				// Tab: going forwards
				if (document.activeElement === this.focusTrap.lastFocusable) {
					e.preventDefault();
					this.focusTrap.firstFocusable?.focus();
				}
			}
		};

		document.addEventListener('keydown', this.focusTrap.handleKeyDown);
		this.focusTrap.isActive = true;

		window.log('info', 'SearchManager', 'Focus trap activated');
	},

	/**
	 * Remove focus trap
	 */
	removeFocusTrap() {
		if (this.focusTrap.isActive && this.focusTrap.handleKeyDown) {
			document.removeEventListener('keydown', this.focusTrap.handleKeyDown);
			this.focusTrap.isActive = false;
			this.focusTrap.triggerElement = null;
			this.focusTrap.firstFocusable = null;
			this.focusTrap.lastFocusable = null;
			this.focusTrap.handleKeyDown = null;
			window.log('info', 'SearchManager', 'Focus trap removed');
		}
	},

	/**
	 * Show search overlay
	 */
	show(triggerElement) {
		const search = window.getElement(this.config.selectors.searchContainer);
		const searchInput = window.getElement(this.config.selectors.searchInput);

		if (!search || !searchInput) {
			window.log('warn', 'SearchManager', 'Required elements not found');
			return;
		}

		document.documentElement.classList.add(this.config.classes.stopScrolling);
		search.classList.add(this.config.classes.open);
		search.setAttribute('aria-hidden', 'false');
		this._updateToggleAria(true);

		// Recherche ouverte : le header doit rester solide, meme sur un hero
		// transparent/light sous le header (sinon le champ de recherche se
		// retrouve sur un fond transparent, peu lisible).
		const header = window.getElement(this.config.selectors.header);
		if (header) {
			header.classList.remove(this.config.classes.headerTransparent, this.config.classes.headerLight);
		}

		if (window.NavigationManager?.showBackgroundFilter) {
			window.NavigationManager.showBackgroundFilter();
		}

		// Focus synchrone, dans le geste utilisateur : iOS n'ouvre le clavier que
		// dans ce cas (un focus différé par setTimeout obligeait à re-toucher le
		// champ). Possible car le conteneur passe en visibility: visible sans délai.
		searchInput.focus({ preventScroll: true });
		this.setupFocusTrap(search, triggerElement);

		window.emit('global:search-opened');
	},

	/**
	 * Hide search overlay
	 */
	hide(keepFilterActive = false, keepStopScrolling = false) {
		const search = window.getElement(this.config.selectors.searchContainer);
		const searchInput = window.getElement(this.config.selectors.searchInput);

		if (!search || !searchInput) {
			window.log('warn', 'SearchManager', 'Required elements not found for hiding');
			return;
		}

		// Ne retirer le scroll-lock QUE si la recherche était réellement ouverte.
		// Sinon (ex: mmHideSearch appelé à l'ouverture du cart pour fermer d'éventuels
		// overlays) on retirerait .stop-scrolling posée par un AUTRE overlay.
		const wasOpen = search.classList.contains(this.config.classes.open);
		if (wasOpen && !keepStopScrolling) {
			document.documentElement.classList.remove(this.config.classes.stopScrolling);
		}
		search.classList.remove(this.config.classes.open);
		search.setAttribute('aria-hidden', 'true');
		this._updateToggleAria(false);

		// Restaure l'etat transparent/light du header selon la position de
		// scroll courante (force en solide a l'ouverture, voir show()).
		window.HeroDotsManager?.syncHeaderState();
		window.ProductHeaderOverlayManager?.syncHeaderState();

		this.removeFocusTrap();

		if (!keepFilterActive && window.NavigationManager?.hideBackgroundFilter) {
			window.NavigationManager.hideBackgroundFilter();
		}

		searchInput.value = '';
		searchInput.dispatchEvent(new Event('input'));

		window.emit('global:search-closed', { keepFilterActive, keepStopScrolling });
	}
};

/*********************************************************************************************
 * 🌍 GLOBAL ACCESS
 */
window.SearchManager = SearchManager;

/*********************************************************************************************
 * 🔄 BACKWARD COMPATIBILITY
 */
window.mmShowSearch = (triggerElement) => SearchManager.show(triggerElement);
window.mmHideSearch = (keepFilter, keepScrolling) => SearchManager.hide(keepFilter, keepScrolling);

/*********************************************************************************************
 * 🎯 AUTO-INITIALIZATION
 */
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => SearchManager.init());
} else {
	SearchManager.init();
}
